import { createSelector } from "@reduxjs/toolkit"

import { RootState } from "@/app/store"

import type { TimeValue } from "@/shared/ui/dropdown"

import { fromIsoDuration } from "@/shared/lib/iso-duration"

const FINITE_DEFAULT: TimeValue = { value: 1, unit: "d" }

const convertDurationFromApi = (iso: string): TimeValue => fromIsoDuration(iso) ?? FINITE_DEFAULT

import { isPunishmentAction, isWholeWordFilter } from "../lib"

// Base selector
export const selectTriggerRule = (state: RootState) => state.prohibitedWords

// Mode selector
export const selectTriggerRuleMode = createSelector([selectTriggerRule], rule => rule.mode)

// Name selector
export const selectRuleName = createSelector([selectTriggerRule], rule => rule.name)

// Word selectors
export const selectProhibitedWords = createSelector([selectTriggerRule], rule => {
  const firstFilter = rule.filter.filters.at(0)
  if (!firstFilter || !isWholeWordFilter(firstFilter)) return []
  return firstFilter.words
})

export const selectProhibitedWordsList = createSelector([selectProhibitedWords], words => words)

// Punishment selector
export const selectPunishment = createSelector([selectTriggerRule], rule => {
  // Find first punishment action
  const action = rule.actions.find(isPunishmentAction)
  if (!action) {
    return {
      type: "mute" as const,
      duration: { value: 1, unit: "h" as const },
      deleteMessages: true,
      skipNotification: false
    }
  }

  const params = action.parameters

  // If warning pipeline is configured, surface it as type "warning"
  const hasWarning = Boolean(params.warning_threshold && params.warning_observation_interval)
  if (hasWarning) {
    return {
      type: "warning" as const,
      duration: params.duration ? convertDurationFromApi(params.duration) : { value: 1, unit: "h" as const },
      deleteMessages: (params.clean_count ?? 0) > 0,
      skipNotification: params.skip_notification,
      warning: {
        count: params.warning_threshold || 3,
        duration: convertDurationFromApi(params.warning_observation_interval || "P1D"),
        punishmentType:
          params.punishment_kind === "mute" || params.punishment_kind === "kick" || params.punishment_kind === "ban"
            ? params.punishment_kind
            : ("mute" as const),
        punishmentDuration: params.duration ? convertDurationFromApi(params.duration) : { value: 1, unit: "h" as const }
      }
    }
  }

  // Non-warning punishments
  return {
    type: params.punishment_kind,
    duration: params.duration ? convertDurationFromApi(params.duration) : { value: 1, unit: "h" as const },
    deleteMessages: (params.clean_count ?? 0) > 0,
    skipNotification: params.skip_notification
  }
})

// Support mode parameters selector
export const selectSupportModeParams = createSelector([selectTriggerRule], rule => rule.supportModeParams)

// Derived actions array selector - builds actions based on current state
export const selectActions = createSelector([selectTriggerRule], rule => {
  return rule.actions
})

// Compute display label for triggers based on mode
export const selectTriggerDisplayLabel = createSelector([selectTriggerRule], rule => {
  // For blacklist, use the name field
  if (rule.mode !== "support_mode" && rule.mode !== "business_mode") {
    return rule.name || "Unnamed List"
  }

  // For support_mode, generate label from trigger words
  const firstFilter = rule.filter.filters.at(0)
  if (!firstFilter || !isWholeWordFilter(firstFilter)) return "Empty Trigger"
  const words = firstFilter.words

  if (words.length === 0) return "Empty Trigger"
  if (words.length === 1) return words[0]
  if (words.length <= 3) return words.join(", ")
  return `${words.slice(0, 3).join(", ")} +${words.length - 3} more`
})
